<template>
    <div id="fusion_create_thread_container" class="relative flex flex-col h-full">

      <!-- Error toast: shown when thread creation fails -->
      <div v-if="errorMessage" class="mx-4 mt-3 mb-[5px] px-3 py-2 bg-danger/10 border border-danger/30 rounded-lg flex items-center gap-2 shrink-0">
        <i class="ri-error-warning-line text-danger text-sm leading-none shrink-0" />
        <span class="flex-1 text-sm text-danger">{{ errorMessage }}</span>
        <i class="ri-close-line text-danger text-sm leading-none cursor-pointer shrink-0" @click="$emit('dismissError')" />
      </div>

      <!-- Channels mode: name field (the public/private picker lives in the header) -->
      <template v-if="mode === 'Channels'">
        <div class="px-4 pb-4 shrink-0">
          <div class="border border-border-subtle rounded-lg px-3 py-2">
            <input type="text" name="fusion_create_thread_channel_name"
                   class="w-full border-none outline-none bg-transparent text-sm text-text-default"
                   v-model="channel_name"
                   :placeholder="$t('messaging.createThread.channelNamePlaceholder')"
                   autocomplete="off" />
          </div>
        </div>
      </template>

      <!-- Participant picker: New Message/Thread recipients, or private channel members -->
      <template v-if="show_participant_picker">
        <!-- To: field with chips + search input -->
        <div class="px-4 pb-4 shrink-0">
          <div class="flex flex-wrap items-center gap-1.5 border border-border-subtle rounded-lg px-3 py-2 min-h-[40px] cursor-text transition-[border-color,box-shadow] focus-within:border-[#0855F9] focus-within:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
               @click="focusSearch">
            <span class="text-sm text-text-subtle shrink-0 leading-6">{{ picker_to_label }}</span>
            <div v-for="personGuid in users_selected" :key="`chip_${personGuid}`"
                 class="flex items-center gap-1 border rounded-full pl-1.5 pr-2 py-0.5 shrink-0 transition-colors"
                 :class="pending_remove_guid === personGuid ? 'bg-[#0855F9] border-[#0855F9]' : 'bg-surface-1 border-border-subtle'">
              <messenger-avatar :name="resolveSelectedUser(personGuid)?.fullName || ''" :size="16" :bg-color="users_selected_colors[personGuid]" />
              <span class="text-xs leading-5 transition-colors"
                    :class="pending_remove_guid === personGuid ? 'text-white' : 'text-text-default'">{{ resolveSelectedUser(personGuid)?.friendlyName || resolveSelectedUser(personGuid)?.fullName || 'Unknown' }}</span>
              <i class="ri-close-line text-xs cursor-pointer transition-colors"
                 :class="pending_remove_guid === personGuid ? 'text-white' : 'text-text-subtle'"
                 @click.stop="toggleUserSelected(personGuid)" />
            </div>
            <input ref="searchInput" type="text"
                   class="flex-1 min-w-[100px] border-none outline-none bg-transparent text-sm text-text-default leading-6"
                   v-model="user_search_query"
                   :placeholder="$FUSarrayHasEntries(users_selected) ? '' : $t('navbar.searchContacts')"
                   autocomplete="off"
                   @keydown="handleSearchKeydown"
                   @blur="pending_remove_guid = null" />
            <i v-if="user_search_query" class="ri-close-circle-line text-sm text-text-subtle cursor-pointer shrink-0" @click.stop="user_search_query=''" />
          </div>
        </div>

        <!-- Search results -->
        <div v-if="user_search_query && $FUSarrayHasEntries(users_available_filtered)"
             class="flex-1 overflow-y-auto px-2">
          <div class="pt-4 shrink-0">
            <div class="border-b border-border-separator pb-1">
              <span class="text-sm text-text-subtle">{{ users_available_filtered.length === 1 ? '1 Result' : users_available_filtered.length + ' Results' }}</span>
            </div>
          </div>
          <div v-for="(user, idx) in users_available_filtered" :key="`search_user_${user.personGuid}`"
               class="flex items-center gap-3 px-2 cursor-pointer hover:bg-black/5"
               @click.prevent="toggleUserSelected(user.personGuid, user_color_assignments[user.personGuid])">
            <fusion-checkbox :model-value="users_selected.indexOf(user.personGuid) >= 0" />
            <messenger-avatar :name="user.fullName" :size="28" :bg-color="user_color_assignments[user.personGuid]" />
            <div class="flex flex-col min-w-0 flex-1 py-4"
                 :class="{'border-b border-border-separator': idx < users_available_filtered.length - 1}">
              <span class="text-sm text-text-default">{{ user.fullName }}</span>
              <span class="text-xs text-text-subtle truncate">{{ user.email }}</span>
            </div>
          </div>
        </div>

        <!-- No results -->
        <p v-else-if="user_search_query && !$FUSarrayHasEntries(users_available_filtered)"
           class="flex-1 text-center text-sm text-text-subtle py-8 px-4">
          No users found.
          <a href="#" class="text-text-link" @click.stop.prevent="user_search_query=''">{{ $t('common.clearSearch') }}</a>
        </p>

        <!-- Empty state: only shown before any recipients are selected -->
        <div v-else-if="!$FUSarrayHasEntries(users_selected)" class="flex-1 flex items-center justify-center px-4">
          <div class="text-center" style="width: 250px;">
            <p class="text-base font-semibold text-text-placeholder leading-snug mb-2">{{ picker_empty_title }}</p>
            <p class="text-sm text-text-placeholder leading-snug">{{ $t('messaging.contacts.searchHint') }}</p>
          </div>
        </div>
      </template>

      <!-- Loading overlay: blocks interaction while thread creation is in flight -->
      <div v-if="isLoading" class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-surface-1/80">
        <div class="fusion-loader loader-xl inline-block mb-2" />
        <span class="text-sm text-text-subtle">{{ loading_subtext }}</span>
      </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import MessengerAvatar from '@components/Messaging/MessengerAvatar.vue';
import FusionCheckbox from '@components/UI/FusionCheckbox.vue';

export default {
  props: {
    userAccessInfo: {
      type: Object,
      required: false,
      default: null
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ['Messages', 'Threads', 'Channels', 'AddToChannel'].includes(value)
    },
    usersInfo: {
      type: Object,
      required: false,
      default: null
    },
    emojiIndex: {
      type: Object,
      required: false,
      default: null
    },
    canUseAvReyAssist: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorMessage: {
      type: String,
      required: false,
      default: '',
    },
  },
  components: {
    'messenger-avatar': MessengerAvatar,
    'fusion-checkbox': FusionCheckbox,
  },
  emits: ['createThread', 'addToChannel', 'dismissError'],
  created() {
    this.users_selected_colors[this.current_user_guid] = this.getAvatarColorForIndex(0);
    this.consumePendingRecipient();
  },
  mounted() {
    window.event_bus.$on('NAVBAR-CREATE-THREAD', this.createMessage);
  },
  beforeUnmount() {
    this.new_thread_create_ready = false;
    this.add_participants_count = 0;
    window.event_bus.$off('NAVBAR-CREATE-THREAD', this.createMessage);
  },
  data() {
    return {
      channel_name: '',
      no_users_message: 'No users available',
      user_search_query: '',
      users_selected: [],
      users_selected_colors: {},
      pending_remove_guid: null,
    }
  },
  computed: {
    ...mapStores(useMainStore),
    pending_new_message_recipient_guid: {
      get() { return this.mainStore.pending_new_message_recipient_guid; },
      set(value) { this.mainStore.pending_new_message_recipient_guid = value; }
    },
    new_thread_create_ready: {
      get() { return this.mainStore.new_thread_create_ready; },
      set(value) { this.mainStore.new_thread_create_ready = value; }
    },
    add_participants_count: {
      // Surfaced to the Navbar so its confirm button can read "Add (N)" in AddToChannel mode.
      get() { return this.mainStore.add_participants_count; },
      set(value) { this.mainStore.add_participants_count = value; }
    },
    active_thread_nav_meta() {
      // Identifies the open conversation (channel vs private thread) so the AddToChannel picker can
      // phrase its empty state correctly.
      return this.mainStore.active_thread_nav_meta;
    },
    current_user_guid() {
      return this.userAccessInfo?.userId || null;
    },
    show_participant_picker() {
      // Recipients for a DM/group, or members for a channel/AddToChannel. Channels are always
      // private when created by hand, so the member picker is always shown — public channels only
      // exist when system-created and are never created through this screen.
      return ['Messages', 'Threads', 'Channels', 'AddToChannel'].indexOf(this.mode) >= 0;
    },
    is_channel_member_picker() {
      return this.mode === 'Channels' || this.mode === 'AddToChannel';
    },
    picker_to_label() {
      return this.is_channel_member_picker ? this.$t('messaging.createThread.addLabel') : this.$t('messaging.createThread.toLabel');
    },
    picker_empty_title() {
      // AddToChannel is shared by private channels and private (object) threads — phrase the empty
      // state to match whichever conversation it was opened from.
      if (this.mode === 'AddToChannel')
        return this.active_thread_nav_meta?.isChannel ? this.$t('messaging.createThread.addPeopleChannel') : this.$t('messaging.createThread.addPeopleThread');
      if (this.is_channel_member_picker) return this.$t('messaging.createThread.addPeopleChannel');
      // Vehicle thread creation ('Threads' mode) is a participant picker, not a DM compose — invite
      // people to the thread rather than "send a message to someone".
      if (this.mode === 'Threads') return this.$t('messaging.createThread.addPeopleThread');
      return this.$t('messaging.contacts.sendMessageTitle');
    },
    enterprise_users() {
      return this.usersInfo?.enterpriseUsers || [];
    },
    ent_users_by_guid() {
      return this.$FUSarrayOfObjectsKeyMap(this.enterprise_users, 'personGuid') || {};
    },
    users_available() {
      return this.enterprise_users.filter(x => x.messagingEnabled);
    },
    users_available_filtered() {
      const self = this;
      const query = this.user_search_query.toLowerCase();
      return this.users_available.filter(user => {
        let userSearchText = `${user.email} ${user.friendlyName} ${user.fullName} ${user.userId}`;
        return user.personGuid !== self.current_user_guid && user.personGuid && user.personGuid.indexOf("system_")<0 && userSearchText.toLowerCase().includes(query);
      }).sort((a,b) => (a.fullName || '').toLowerCase().localeCompare((b.fullName || '').toLowerCase()));
    },
    message_send_ready() {
      switch (this.mode) {
        case 'Messages':
        case 'Threads':
        case 'AddToChannel':
          return this.$FUSarrayHasEntries(this.users_selected);
        case 'Channels':
          return Boolean(this.channel_name);
      }
    },
    loading_subtext() {
      switch (this.mode) {
        case 'Messages':    return this.$t('messaging.createThread.creatingConversation');
        case 'Channels':    return this.$t('messaging.createThread.creatingChannel');
        case 'AddToChannel': return this.$t('messaging.createThread.addingParticipants');
        default:            return this.$t('messaging.createThread.creatingThread');
      }
    },
    user_color_assignments() {
      const self = this;
      const assignments = {};
      const usedIndices = new Set();

      // First, map locked colors to their indices to track which indices are used
      Object.values(this.users_selected_colors).forEach(colorHex => {
        const index = this.getIndexFromColor(colorHex);
        if (index !== -1) {
          usedIndices.add(index);
        }
      });

      let nextAvailableIndex = 0;

      // Helper to get next unused index
      const getNextIndex = () => {
        while (usedIndices.has(nextAvailableIndex))
          nextAvailableIndex++;

        return nextAvailableIndex++;
      };

      // Include the special user from created() if it exists
      if (this.current_user_guid && this.users_selected_colors[this.current_user_guid]) {
        assignments[this.current_user_guid] = this.users_selected_colors[this.current_user_guid];
      }

      // Process each user
      this.users_available_filtered.forEach(user => {
        const personGuid = user.personGuid;

        if (self.users_selected_colors.hasOwnProperty(personGuid)) {
          // User is locked in - use their locked color
          assignments[personGuid] = self.users_selected_colors[personGuid];
        }
        else {
          // User is not locked - assign next available index
          const index = getNextIndex();
          assignments[personGuid] = self.getAvatarColorForIndex(index);
        }
      });

      return assignments;
    },
  },
  methods: {
    resolveSelectedUser(personGuid) {
      // users_selected can briefly (or persistently) hold a guid that isn't in ent_users_by_guid:
      // a recipient preselected via consumePendingRecipient before usersInfo has loaded, or one
      // sourced from a list other than usersInfo.enterpriseUsers. Return null rather than letting
      // the chip template dereference undefined (which throws and blanks the whole composer). The
      // chip fills in on its own once the matching user resolves, since ent_users_by_guid is a
      // computed over the reactive usersInfo prop.
      return this.ent_users_by_guid[personGuid] || null;
    },
    consumePendingRecipient() {
      // If a sibling flow (e.g. EnterpriseContactDetails → Send Message) stashed a guid for us
      // to preselect, pick it up here.
      const recipient = this.pending_new_message_recipient_guid;
      if(!recipient) return;
      // Clear the store handoff IMMEDIATELY, on every exit path that "saw" the guid. This
      // prevents a stale guid from leaking into the next CreateThread mount — e.g. if this
      // mount is in Channels mode (which doesn't preselect), the guid would otherwise survive
      // and pre-select an unexpected recipient on the next Messages-mode open.
      this.pending_new_message_recipient_guid = null;
      // Preselect only applies in Messages/Threads modes.
      if(['Messages','Threads'].indexOf(this.mode) < 0) return;
      // Skip if it's the current user (would put them on both sides) or already selected.
      if(recipient === this.current_user_guid) return;
      if(this.users_selected.indexOf(recipient) < 0) {
        // Pick the first unused color index (1 onward; 0 is reserved for the sender).
        this.toggleUserSelected(recipient, this.getAvatarColorForIndex(1));
      }
    },
    handleSearchKeydown(e) {
      if (e.key !== 'Backspace' || this.user_search_query) {
        // Any non-backspace key, or backspace while field has text — clear highlight only
        this.pending_remove_guid = null;
        return;
      }
      // Backspace with empty input
      if (!this.$FUSarrayHasEntries(this.users_selected)) return;
      if (this.pending_remove_guid) {
        // Second backspace — remove the highlighted chip
        this.toggleUserSelected(this.pending_remove_guid);
      } else {
        // First backspace — highlight the last chip
        this.pending_remove_guid = this.users_selected[this.users_selected.length - 1];
      }
    },
    toggleUserSelected(userGuid, setColor=null) {
      this.pending_remove_guid = null;
      if(this.users_selected.indexOf(userGuid)<0) {
        this.users_selected_colors[userGuid] = setColor;
        this.users_selected.push(userGuid);
      }
      else {
        this.users_selected = this.users_selected.filter(x => x !== userGuid);
        delete this.users_selected_colors[userGuid];
      }
      if (this.mode === 'AddToChannel') {
        // Keep the Navbar's "Add (N)" button label in sync with the live selection.
        this.add_participants_count = this.users_selected.length;
      }
    },
    createMessage() {
      if (!this.message_send_ready) return;
      // Adding members to an existing channel is a distinct action — emit the selected users
      // for MessengerWindow to invite, rather than creating a new thread/channel.
      if (this.mode === 'AddToChannel') {
        this.$emit('addToChannel', this.users_selected);
        return;
      }
      const createThreadData = {
        message: '',
        users: this.users_selected,
        channelName: this.channel_name,
        // Hand-created channels are always private; public channels are system-created only.
        isPublic: false,
      };
      this.$emit('createThread', createThreadData);
    },
    cancelMessage() {
      this.$emit('createThread', null);
    },
    focusSearch() {
      this.$refs.searchInput?.focus();
    },
    getIndexFromColor(colorHex) {
      // First check if it's a base color
      const baseIndex = this.$FUSfusionAvatarColorsHex.indexOf(colorHex);
      if (baseIndex !== -1) {
        return baseIndex;
      }

      return -1;
    },
    getAvatarColorForIndex(index) {
      const colorIndex = index % this.$FUSfusionAvatarColorsHex.length;
      const cycle = Math.floor(index / this.$FUSfusionAvatarColorsHex.length);
      const baseColor = this.$FUSfusionAvatarColorsHex[colorIndex];

      // Apply variation on subsequent cycles
      if(cycle === 0)
        return baseColor;
      else {
        // If color is being reused, change it slightly
        let hex = baseColor;
        hex = hex.replace('#', '');

        // Parse RGB values
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);

        // Adjust brightness - alternate between lighter and darker
        const adjustment = cycle % 2 === 1 ? 30 : -30;
        r = Math.max(0, Math.min(255, r + adjustment * cycle));
        g = Math.max(0, Math.min(255, g + adjustment * cycle));
        b = Math.max(0, Math.min(255, b + adjustment * cycle));

        // Convert back to hex
        return '#' + [r, g, b].map(x => {
          const hex = x.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        }).join('');
      }
    },
  },
  watch: {
    message_send_ready(val) {
      this.new_thread_create_ready = val;
    },
  },
}
</script>
